/**
 * @fileoverview Review & Analysis agents — frontend display registry
 * @module jobs/phases/review/reviewRegistry
 *
 * Single source of truth for the agents shown under Review & Analysis:
 *   - URL `slug` used in /review/$agent
 *   - human-readable `label`
 *   - lucide `icon` for the sidebar sub-item, overview card, detail header
 *   - `description` shown on the overview row
 *   - `dataAgentId` — manifest id used by the detail route to dispatch to
 *     the live AgentDetailRouter (the agents currently surfaced here are
 *     Data-section agents re-homed under R&A)
 *
 * Used by:
 * - app-sidebar (Review & Analysis sub-items)
 * - review.index route (overview cards)
 * - review.$agent route (detail dispatch)
 * - breadcrumb-header (human label for $agent in the breadcrumb)
 */

import {
  ChartColumnDecreasing,
  FileSpreadsheet,
  Flag,
  Scale,
  BookCheck,
  type LucideIcon,
} from "lucide-react";
import { caseTypeFromAppointmentType } from "@/lib/precedent-templates";
import type { FinancialAnalysisSection } from "@/components/jobs/phases/gather/summarization/financialAnalysis/types";

export interface ReviewAgentEntry {
  /** URL slug used in /dashboard/jobs/$jobId/review/$agent. */
  slug: string;
  /** Human-readable label shown in the sidebar, breadcrumb, and card. */
  label: string;
  /** Lucide icon rendered everywhere this agent appears. */
  icon: LucideIcon;
  /** Short blurb used on the overview card body. */
  description: string;
  /** Blurb on a personal or business bankruptcy job (MOR-1260); falls back to `description`. */
  bankruptcyDescription?: string;
  /**
   * Manifest id of the underlying Data-section agent. The /review/$agent
   * route uses it to render the live AgentDetailRouter view.
   */
  dataAgentId: string;
  /**
   * Entries with their own dedicated workspace (not an AgentDetailRouter
   * dispatch). The /review/$agent route branches on this.
   */
  customView?: boolean;
  /**
   * Hidden entirely on personal-insolvency (bankrupt estate) jobs. Used for
   * agents whose analysis has no bankruptcy analogue yet (e.g. Corporations
   * Act offence heads). Gated via {@link reviewAgentsForJob} and
   * {@link isReviewAgentVisibleOnJob}.
   */
  hiddenForBankruptcy?: boolean;
  /**
   * Which destination of the shared financial-analysis task this entry
   * renders (MOR-1280). Both entries point at `sum_financial_analysis`.
   */
  financialSection?: FinancialAnalysisSection;
}

export const REVIEW_AGENTS: ReadonlyArray<ReviewAgentEntry> = [
  {
    slug: "books-and-records",
    label: "Books & Records",
    icon: BookCheck,
    description:
      "Assesses the adequacy of the company's books and records under s286 / s588E and flags outstanding records to request.",
    bankruptcyDescription:
      "Assesses whether the bankrupt kept the books and records usual and proper for any business carried on, which drives the s 120(3A) and s 121(4A) presumptions.",
    dataAgentId: "sum_books_and_records",
    customView: true,
  },
  // Historical Financial Analysis and Solvency Analysis are two destinations
  // over the same financial-analysis task (MOR-1280): the first is the
  // report-style historical statements and charts, the second lands on the
  // solvency indicators. The Solvency entry keeps the original slug so
  // existing links and bookmarks still resolve.
  {
    slug: "historical-financial-analysis",
    label: "Historical Financial Analysis",
    icon: FileSpreadsheet,
    description:
      "Period-by-period income statement, balance sheet, cash flow and liquidity analysis drawn from the company's financial records.",
    dataAgentId: "sum_financial_analysis",
    financialSection: "historical",
  },
  {
    slug: "financial-analysis",
    label: "Solvency Analysis",
    icon: ChartColumnDecreasing,
    description:
      "Assesses the solvency indicators against the historical financial analysis and surfaces the insolvency signals running through the ledger.",
    dataAgentId: "sum_financial_analysis",
    financialSection: "solvency",
  },
  {
    slug: "flagged-transactions",
    label: "Recoverable Transactions",
    icon: Flag,
    description:
      "Identifies potential recoverable transactions (insolvent trading, unfair preference, uncommercial transactions, etc).",
    bankruptcyDescription:
      "Identifies potential recoverable transfers under the Bankruptcy Act: undervalued transfers (s 120), preferences (s 122) and transfers to defeat creditors (s 121).",
    dataAgentId: "sum_flagged_master",
    customView: true,
  },
  {
    slug: "civil-criminal-offences",
    label: "Civil & Criminal Offences",
    icon: Scale,
    description:
      "Characterises potential offences and civil-penalty contraventions per respondent (director's duties, insolvent trading, phoenix, books & records, ROCAP) over the review-agent outputs.",
    bankruptcyDescription:
      "Bankruptcy Act offence heads are not yet modelled; this section is deferred on bankruptcy jobs.",
    dataAgentId: "sum_offence_master",
    customView: true,
    // Bankruptcy Act offence heads aren't modelled yet, so this section isn't
    // useful on a bankruptcy job — hide it for the time being (MOR follow-up
    // will re-enable once bankruptcy offences are supported).
    hiddenForBankruptcy: true,
  },
];

/** Review slug for a financial-analysis destination. */
export function reviewSlugForFinancialSection(
  section: FinancialAnalysisSection,
): string {
  return (
    REVIEW_AGENTS.find((a) => a.financialSection === section)?.slug ??
    "financial-analysis"
  );
}

/** slug → entry. */
export const REVIEW_AGENTS_BY_SLUG: ReadonlyMap<string, ReviewAgentEntry> =
  new Map(REVIEW_AGENTS.map((a) => [a.slug, a]));

/**
 * Review agents shown in the sidebar and on the Investigations overview for a
 * given job. Personal-insolvency jobs drop any agent flagged
 * `hiddenForBankruptcy` (Corporations Act-only analyses).
 */
export function reviewAgentsForJob(
  appointmentType?: string,
): ReadonlyArray<ReviewAgentEntry> {
  const isBankruptcy =
    caseTypeFromAppointmentType(appointmentType) === "personal";
  return isBankruptcy
    ? REVIEW_AGENTS.filter((agent) => !agent.hiddenForBankruptcy)
    : REVIEW_AGENTS;
}

/**
 * Whether a Review agent (by URL slug) should be reachable on this job. Agents
 * flagged `hiddenForBankruptcy` are hidden on personal-insolvency jobs.
 */
export function isReviewAgentVisibleOnJob(
  slug: string,
  appointmentType?: string,
): boolean {
  const entry = REVIEW_AGENTS_BY_SLUG.get(slug);
  if (
    entry?.hiddenForBankruptcy &&
    caseTypeFromAppointmentType(appointmentType) === "personal"
  ) {
    return false;
  }
  return true;
}
